/**
 * 宮崎県の背景ツアー「宮崎めぐり」コンテンツ。
 *
 * 軸は時間帯。日向岬の夜明けから、都井岬の日没へ。
 *
 * 神話をどう書くかがこの県の要点。
 *  - 神話は「伝えられている」という形で書く。歴史的事実として断定しない。
 *  - 「天孫降臨の地は高千穂」と断定しない。霧島にも同じ伝承がある。
 *  - 天岩戸神社と鵜戸神宮はいまも祈りの場。「パワースポット」「願いが叶う」は使わない。
 *    ただし日向岬の「願いが叶うクルスの海」はそれが正式名称なので、名前としては使う。
 *    本文は「言い伝えられています」の形に固定する。
 *
 * そのほか調査で出てきた「書いてはいけないこと」。
 *  - 綾の照葉大吊橋の「世界一」は、同じ綾町公式のページ間で記述が食い違う。使わない。
 *  - 馬ヶ背を「柱状節理」と説明しない。観光協会のページに岩質の記載がない。
 *  - 青島の天然記念物としての正式名称は「青島の隆起海床と奇形波蝕痕」。俗称と並べて書く。
 *  - 真名井の滝の「日本の滝100選」は民間の選定。触れるなら選ばれている、まで。
 *  - えびの高原の噴火警戒レベルは変わるので、数字を豆知識に書かない。
 *  - 関之尾滝の遊歩道の通行可否は都城市の公式ページ間で食い違う。豆知識にしない。
 *  - 西都原の「菜の花まつり」「桜まつり」の時期は公式で裏が取れない。書かない。
 */

import type { StageTour } from '../types';

export const miyazakiSceneryTour: StageTour = {
  scenes: [
    {
      id: 'umagase',
      name: '日向岬 馬ヶ背',
      area: '日向市・夜明け',
      image: '/assets/scenery/miyazaki/scenes/01_umagase.webp',
      hotspots: [
        {
          id: 'umagase-tenbo',
          x: 0.46,
          y: 0.38,
          title: '突き出した展望台',
          body: '日向岬の先端に馬ヶ背の断崖があり、2022年にガラス張りの突き出し展望台「スケルッチャ！」ができました。そこから2分ほど進むと日向岬展望台に出ます。目の前は太平洋です。',
          source: 'https://hyuga.or.jp/app/spots/view/43',
          mapQuery: '馬ヶ背 日向岬',
        },
        {
          id: 'cruz-no-umi',
          x: 0.72,
          y: 0.3,
          title: '十文字に裂けた岩礁',
          body: '東西200メートル、南北220メートルにわたって岩礁が裂け、上から見ると十文字に見えます。ポルトガル語で十文字を意味する「クルス」から名づけられ、願いが叶うと言い伝えられています。',
          source: 'https://hyuga.or.jp/app/spots/view/7',
          mapQuery: '願いが叶うクルスの海',
        },
      ],
    },
    {
      id: 'amanoiwato',
      name: '天岩戸神社と天安河原',
      area: '高千穂町・朝',
      image: '/assets/scenery/miyazaki/scenes/02_amanoiwato.webp',
      hotspots: [
        {
          id: 'iwato-goshintai',
          x: 0.46,
          y: 0.36,
          title: '御神体は建物ではない',
          body: '西本宮は、天照皇大神が御隠れになったと伝えられる天岩戸という洞窟そのものを御神体としてお祀りする神社です。神社は「古より其の御神域を御神体としてお祭り致して居ります」と説明しています。',
          source: 'https://amanoiwato-jinja.jp/pages/31/',
          mapQuery: '天岩戸神社',
        },
        {
          id: 'iwato-yohai',
          x: 0.72,
          y: 0.46,
          title: '神職が案内する遥拝',
          body: '天岩戸は、西本宮拝殿の裏にある遥拝所から、神職の案内で拝みます。所要は10分から15分ほどで、案内に料金はかかりません。希望する人は開始時間までに休憩所に集まります。',
          source: 'https://amanoiwato-jinja.jp/pages/33/',
          mapQuery: '天岩戸神社',
        },
        {
          id: 'gyobogaiwaya',
          x: 0.22,
          y: 0.44,
          title: '仰慕窟という大きな岩屋',
          body: '天安河原宮は思兼神と八百萬神をお祀りしています。仰慕窟と呼ばれる、間口40メートル、奥行30メートルの大きな洞窟があります。',
          source: 'https://amanoiwato-jinja.jp/pages/31/',
          mapQuery: '天安河原',
        },
      ],
    },
    {
      id: 'takachiho-kyo',
      name: '高千穂峡',
      area: '高千穂町・午前',
      image: '/assets/scenery/miyazaki/scenes/03_takachiho_kyo.webp',
      hotspots: [
        {
          id: 'takachiho-kasairyu',
          x: 0.44,
          y: 0.3,
          title: '火砕流が冷えて固まった谷',
          body: '阿蘇山の大きな噴火で流れ出た火砕流が冷え固まり、長い年月をかけて侵食されてこの深い谷ができました。1934年に「五箇瀬川峡谷」として国の名勝・天然記念物に指定されています。',
          source: 'https://www.town-takachiho.jp/top/kanko_bunka/kanko_supotto/781.html',
          mapQuery: '高千穂峡',
        },
        {
          id: 'takachiho-7km',
          x: 0.7,
          y: 0.24,
          title: '崖は7キロ続く',
          body: '高いところで100メートル、平均80メートルの断崖が、東西およそ7キロにわたって続きます。真名井の滝はおよそ17メートルの高さから水面に落ちます。',
          source: 'https://takachiho-kanko.info/sightseeing/18/',
          mapQuery: '真名井の滝',
        },
        {
          id: 'takachiho-boat',
          x: 0.24,
          y: 0.48,
          title: 'ボートはネット予約だけ',
          body: '貸しボートは電話予約を受け付けておらず、インターネット予約のみです。河川が増水しているとき、定期の安全点検のとき、高千穂町で震度3以上の地震があったときは営業を休みます。',
          source: 'https://takachiho-kanko.info/boat/detail.php',
          mapQuery: '高千穂峡 貸しボート',
        },
      ],
    },
    {
      id: 'aoshima',
      name: '青島と鬼の洗濯板',
      area: '宮崎市・干潮の昼',
      image: '/assets/scenery/miyazaki/scenes/04_aoshima.webp',
      hotspots: [
        {
          id: 'aoshima-hashokukon',
          x: 0.5,
          y: 0.5,
          title: '昭和9年指定の波の跡',
          body: '正式名称は「青島の隆起海床と奇形波蝕痕」で、1934年5月1日に国の天然記念物に指定されました。傾いた砂岩と泥岩の互層が波に削られ、硬さの違いで凹凸ができたものです。',
          source: 'https://www.city.miyazaki.miyazaki.jp/culture/art/207.html',
          mapQuery: '鬼の洗濯板 青島',
        },
        {
          id: 'aoshima-biro',
          x: 0.46,
          y: 0.26,
          title: '特別天然記念物のビロウ林',
          body: '「青島亜熱帯性植物群落」は1952年3月29日に国の特別天然記念物に指定されました。ビロウの成木はおよそ4,300本あり、島は面積40,000平方メートル、周囲860メートルです。',
          source: 'https://www.city.miyazaki.miyazaki.jp/culture/art/2150.html',
          mapQuery: '青島',
        },
        {
          id: 'aoshima-jinja',
          x: 0.74,
          y: 0.34,
          title: '海幸彦・山幸彦の話の場所',
          body: '青島神社の御祭神は彦火火出見命、豊玉姫命、塩筒大神です。神社は彦火火出見命を「海幸彦・山幸彦のお話の山幸彦」と説明しています。奉祀の年代は明らかでないとしています。',
          source: 'https://aoshima-jinja.jp/history/',
          mapQuery: '青島神社',
        },
      ],
    },
    {
      id: 'saitobaru',
      name: '西都原古墳群',
      area: '西都市・春の昼',
      image: '/assets/scenery/miyazaki/scenes/05_saitobaru.webp',
      hotspots: [
        {
          id: 'saito-319',
          x: 0.46,
          y: 0.44,
          title: '319基の古墳',
          body: '特別史跡の西都原古墳群は、陵墓参考地の男狭穂塚・女狭穂塚を加えて319基あります。3世紀末から7世紀にかけて築かれ、指定面積は58ヘクタールを超えます。',
          source: 'https://saito-muse.pref.miyazaki.jp/web/guidance.html',
          mapQuery: '西都原古墳群',
        },
        {
          id: 'saito-1952',
          x: 0.74,
          y: 0.34,
          title: '昭和27年に特別史跡',
          body: '指定年月日は1952年3月29日です。台帳の上では317基で、陵墓参考地2基を加えた319基が公的な数とされています。',
          source: 'https://www.city.saito.lg.jp/post_270.html',
          mapQuery: '西都原古墳群',
        },
        {
          id: 'saito-osaho',
          x: 0.22,
          y: 0.32,
          title: '南九州いちばんの2基',
          body: '男狭穂塚と女狭穂塚は5世紀前半に築かれた南九州の盟主墳です。女狭穂塚は九州最大の前方後円墳、男狭穂塚は列島最大の帆立貝形古墳とされています。',
          source: 'https://saito-muse.pref.miyazaki.jp/web/guidance.html',
          mapQuery: '男狭穂塚 女狭穂塚',
        },
      ],
    },
    {
      id: 'teruha-otsuribashi',
      name: '綾の照葉大吊橋',
      area: '綾町・初夏の昼',
      image: '/assets/scenery/miyazaki/scenes/06_teruha_otsuribashi.webp',
      hotspots: [
        {
          id: 'teruha-142m',
          x: 0.48,
          y: 0.36,
          title: '谷の上142メートル',
          body: '長さ250メートル、高さ142メートル、幅1.2メートルの吊橋です。一度に800人まで渡れます。営業は午前9時から午後5時までで、通行は有料です。',
          source: 'https://www.town.aya.miyazaki.jp/site/tour-guide/1099.html',
          mapQuery: '綾の照葉大吊橋',
        },
        {
          id: 'teruha-shoyoju',
          x: 0.74,
          y: 0.48,
          title: '照葉樹林とは',
          body: '高木はカシ・シイ・タブ・クスなど、亜高木はヤブツバキ・モッコク・ヤマモモ、低木はサカキ・サザンカなど。綾町は綾川渓谷の照葉樹林を「他に類を見ることができない原生的なすぐれた樹林」と説明しています。',
          source: 'https://www.town.aya.miyazaki.jp/soshiki/eco/1097.html',
          mapQuery: '綾の照葉樹林',
        },
        {
          id: 'teruha-mab',
          x: 0.22,
          y: 0.5,
          title: 'ユネスコエコパーク',
          body: '保存・開発・研究という三つの機能と、三つのゾーンに分けた土地利用のしくみで運営されています。推薦は2011年9月28日に決定しました。',
          source: 'https://www.town.aya.miyazaki.jp/soshiki/eco/1012.html',
          mapQuery: '綾町 ユネスコエコパーク',
        },
      ],
    },
    {
      id: 'ebino-kogen',
      name: 'えびの高原と大浪池',
      area: 'えびの市・秋の午後',
      image: '/assets/scenery/miyazaki/scenes/07_ebino_kogen.webp',
      hotspots: [
        {
          id: 'kirishima-kakoko',
          x: 0.48,
          y: 0.4,
          title: '火口湖と噴気の高原',
          body: '環境省は霧島地域について「多様な地形を有する火山や火口湖、噴気現象、温泉が景観を特徴づけています」と記しています。韓国岳の山頂などからは、桜島や開聞岳まで列状に並ぶ火山を望めます。',
          source: 'https://www.env.go.jp/park/kirishima/point/index.html',
          mapQuery: '大浪池',
        },
        {
          id: 'kirishima-tozando',
          x: 0.74,
          y: 0.3,
          title: '登れる道は変わる',
          body: 'ここは活動している火山です。噴火警戒レベルによって使える登山道が変わり、硫黄山の周辺は利用できない時期があります。出かける前に、えびの市と気象庁の最新の発表を必ず確かめてください。',
          source: 'https://www.city.ebino.lg.jp/soshiki/kankoshoko/3/5234.html',
          mapQuery: 'えびの高原',
        },
        {
          id: 'kirishima-kazangasu',
          x: 0.22,
          y: 0.34,
          title: '硫黄山の火口に注意',
          body: '気象庁は、硫黄山の火口内と、その西側の噴気地帯からおおむね100メートルの範囲では、熱水や熱泥が飛び散るおそれがあるとして注意を呼びかけています。火山ガスにも注意が必要です。',
          source: 'https://www.data.jma.go.jp/vois/data/report/activity_info/552.html',
          mapQuery: 'えびの高原 硫黄山',
        },
      ],
    },
    {
      id: 'sekinoo',
      name: '関之尾滝と甌穴群',
      area: '都城市・夕方前',
      image: '/assets/scenery/miyazaki/scenes/08_sekinoo.webp',
      hotspots: [
        {
          id: 'sekinoo-taki',
          x: 0.46,
          y: 0.42,
          title: '横に広い滝',
          body: '関之尾滝は幅40メートル、落差18メートルあります。このほかに、明治時代に岩を掘って作られた人工の滝である男滝と女滝があります。',
          source: 'https://www.city.miyakonojo.miyazaki.jp/site/kanko/10417.html',
          mapQuery: '関之尾滝',
        },
        {
          id: 'sekinoo-oketsu',
          x: 0.72,
          y: 0.3,
          title: '千数百のまるい穴',
          body: '国の天然記念物です。長さおよそ600メートル、幅およそ80メートルの川床に、千数百もの甌穴が広がります。砂や小石を巻き込んだ急流が渦を巻き、軟らかい岩を丸く削ってできたものです。',
          source: 'https://www.city.miyakonojo.miyazaki.jp/site/bunkazai/2083.html',
          mapQuery: '関之尾の甌穴群',
        },
      ],
    },
    {
      id: 'udo-jingu',
      name: '鵜戸神宮',
      area: '日南市・夕暮れ',
      image: '/assets/scenery/miyazaki/scenes/09_udo_jingu.webp',
      hotspots: [
        {
          id: 'udo-dokutsu',
          x: 0.46,
          y: 0.4,
          title: '洞窟の中の本殿',
          body: '御祭神は日子波瀲武鸕鷀草葺不合尊です。神宮は「自然の神秘な洞窟の中に、朱塗りの色あざやかにご鎮座されている」と記し、この洞窟を主祭神の産殿の址と伝えられる霊地としています。',
          source: 'https://www.udojingu.or.jp/goyuisyo/',
          mapQuery: '鵜戸神宮',
        },
        {
          id: 'udo-undama',
          x: 0.72,
          y: 0.5,
          title: '運玉を投げる',
          body: '亀石は豊玉姫命を乗せてきた亀と伝えられ、長さおよそ8メートル、背に60センチ四方のくぼみがあります。粘土を丸めて焼いた運玉を、男性は左手、女性は右手で投げる風習があります。',
          source:
            'https://www.udojingu.or.jp/history/%E4%BA%80%E7%9F%B3%E3%81%A8%E9%81%8B%E7%8E%89/',
          mapQuery: '鵜戸神宮 亀石',
        },
        {
          id: 'udo-honden',
          x: 0.22,
          y: 0.32,
          title: '権現造りの本殿',
          body: '1995年3月23日に県の指定を受けています。正徳元年、1711年に飫肥藩の5代藩主が再興したもので、その後の改修を経ても権現造りの様式が保たれています。',
          source:
            'https://www.udojingu.or.jp/history/%E7%9C%8C%E6%8C%87%E5%AE%9A-%E5%BB%BA%E9%80%A0%E7%89%A9%E3%80%8C%E9%B5%9C%E6%88%B8%E7%A5%9E%E5%AE%AE%E6%9C%AC%E6%AE%BF%E3%80%8D/',
          mapQuery: '鵜戸神宮',
        },
      ],
    },
    {
      id: 'toimisaki',
      name: '都井岬',
      area: '串間市・日没',
      image: '/assets/scenery/miyazaki/scenes/10_toimisaki.webp',
      hotspots: [
        {
          id: 'misaki-uma',
          x: 0.46,
          y: 0.44,
          title: '昭和28年に天然記念物になった馬',
          body: '御崎馬は1953年に純粋な日本在来馬として国の天然記念物に指定されました。極めて粗放な周年放牧で飼われ、繁殖はまったく自然にまかされてきました。元禄10年に開かれた藩営牧場が300年以上続いています。',
          source: 'https://www.city.kushima.lg.jp/enjoy/touristspots/cat2/post-15.html',
          mapQuery: '都井岬 御崎馬',
        },
        {
          id: 'misaki-chikayoranai',
          x: 0.72,
          y: 0.5,
          title: '馬には近づかない',
          body: '串間市は「危険ですので馬には近づかないで下さい」と呼びかけています。馬の視界は350度あり、正面を向いたまま自分の後ろ足まで見えます。後ろから近づくことは避けてください。',
          source: 'https://www.city.kushima.lg.jp/enjoy/touristspots/cat2/post-15.html',
          mapQuery: '都井岬',
        },
        {
          id: 'misaki-sotetsu',
          x: 0.22,
          y: 0.34,
          title: 'ソテツ自生の北限',
          body: '都井岬ソテツ自生地は1952年3月29日に国の特別天然記念物に指定されました。御崎神社の周辺におよそ3,000本が自生し、ソテツ群落の自生の北限とされています。',
          source: 'https://www.city.kushima.lg.jp/enjoy/bunka/cat3/post-9.html',
          mapQuery: '都井岬 ソテツ自生地',
        },
      ],
    },
  ],
};
